import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { X } from 'lucide-react';

interface HrModalProps {
  open: boolean;
  title: string;
  subtitle?: string;
  icon?: LucideIcon;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
  /** Optional tab strip rendered between the header and the scrollable body. */
  tabs?: ReactNode;
  /** Tailwind max-width class, defaults to `max-w-lg`. */
  maxWidth?: string;
  /** Accent classes for the header icon chip. */
  iconClassName?: string;
}

/**
 * Shared shell for the HR record dialogs. Provides a consistent header,
 * scrollable body and sticky footer while remaining portal/overlay friendly
 * with `SearchableSelect` (its dropdown stops mousedown propagation).
 */
export function HrModal({
  open,
  title,
  subtitle,
  icon: Icon,
  onClose,
  children,
  footer,
  tabs,
  maxWidth = 'max-w-lg',
  iconClassName = 'bg-primary/10 text-primary',
}: HrModalProps) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`bg-surface rounded-xl border border-border shadow-lg w-full ${maxWidth} max-h-[92vh] flex flex-col`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 p-4 border-b border-border">
          <div className="flex items-center gap-3 min-w-0">
            {Icon && (
              <div className={`w-9 h-9 shrink-0 rounded-lg flex items-center justify-center ${iconClassName}`}>
                <Icon size={18} />
              </div>
            )}
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-text truncate">{title}</h2>
              {subtitle && <p className="text-xs text-text-muted truncate">{subtitle}</p>}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded-lg hover:bg-background transition-colors"
          >
            <X size={18} className="text-text-muted" />
          </button>
        </div>

        {tabs && (
          <div className="flex items-center gap-1 overflow-x-auto border-b border-border px-2">
            {tabs}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 space-y-4">{children}</div>

        <div className="flex flex-wrap items-center justify-end gap-3 p-4 border-t border-border">{footer}</div>
      </div>
    </div>
  );
}
